import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  COMMAND_PRIORITY_CRITICAL,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from 'lexical'
import { memo, useCallback, useEffect } from 'react'
import { $getSelection, $isRangeSelection, $isRootNode } from 'lexical'
import { $isListItemNode, $isListNode } from '@lexical/list'
import { mergeRegister } from '@lexical/utils'

export const ListIndentOutdentPlugin = memo(() => {
  const [editor] = useLexicalComposerContext()

  // INDENT_CONTENT_COMMAND の処理を必要に応じてrejectするリスナー
  // rejectする場合はtrueを返す
  const rejectIndentCommandIfNeededListener = useCallback(() => {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) {
      // 範囲選択でない場合は伝播を停止せずインデント操作を許可する
      return false
    }

    const selectionNodes = selection.getNodes()
    if (selectionNodes.length === 0) {
      // ノードがない場合は伝播を停止せずインデント操作を許可する
      return false
    }

    // インデントを禁止するかどうかのフラグ
    let isIndentPrevented = false
    for (const node of selectionNodes) {
      let parent = node.getParent()
      // ノード自身かその親がリストアイテムノードであるか確認
      if (!$isListItemNode(node) && !$isListItemNode(parent)) {
        // どちらでもない場合はインデント禁止するかどうかを判定する必要がないのでcontinueする
        continue
      }

      // リストに文字列が含まれるかどうかによって、listItemかテキストノードとなる
      // リストアイテムノードの場合はそのノードを、そうでない場合は親ノードのlistItemを処理対象とする
      // 例）
      //  リストに文字が入力されている場合のノード構造:
      //    listItemNode
      //      - ExtendedTextNode ← 変数nodeにはこれが入っている
      //  リストに文字が入力されていない場合のノード構造:
      //    listItemNode ← 変数nodeにはこれが入っている
      let processTargetNode
      if ($isListItemNode(node)) {
        processTargetNode = node
      } else {
        processTargetNode = parent
      }

      while (processTargetNode) {
        // ルートノードに到達したらループ終了
        if ($isRootNode(processTargetNode)) {
          break
        }

        // リストアイテムノードの場合、前方の兄弟ノードを取得する
        if ($isListItemNode(processTargetNode)) {
          // 処理対象のリストアイテムノードの、前方の兄弟ノードを取得
          const previousNode = processTargetNode.getPreviousSibling()

          // 前方の兄弟ノードが存在し、かつリストアイテムノードであるかどうかを確認
          if (previousNode && $isListItemNode(previousNode)) {
            // 前方の兄弟ノードがリストアイテムの場合は、ひとつ飛ばしのインデントになることはないため、インデントOK
            isIndentPrevented = false
            break
          } else {
            // 前方の兄弟ノードがリストアイテムでない場合は、ひとつ飛ばしのインデント構造ができてしまうため、インデント禁止
            isIndentPrevented = true
            break
          }
        }

        processTargetNode = processTargetNode.getParent()
      }

      // インデント禁止が決定したらループを抜ける
      if (isIndentPrevented) {
        break
      }
    }

    // インデントを禁止する場合はコマンドの伝播を停止するためtrueを返す
    if (isIndentPrevented) {
      return true
    }

    // インデントを禁止しない場合はコマンドの伝播を停止しないためfalseを返す
    return false
  }, [])

  // OUTDENT_CONTENT_COMMAND の処理を必要に応じてrejectするリスナー
  // rejectする場合はtrueを返す
  const rejectOutdentCommandIfNeededListener = useCallback(() => {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) {
      // 範囲選択でない場合は伝播を停止せずアウトデント操作を許可する
      return false
    }

    const selectionNodes = selection.getNodes()
    if (selectionNodes.length === 0) {
      // ノードがない場合は伝播を停止せずアウトデント操作を許可する
      return false
    }

    // アウトデントを禁止するかどうかのフラグ
    let isOutdentPrevented = false
    for (const node of selectionNodes) {
      let parent = node.getParent()
      // ノード自身かその親がリストアイテムノードであるか確認
      if (!$isListItemNode(node) && !$isListItemNode(parent)) {
        // どちらでもない場合は、アウトデント禁止するかどうかを判定する必要がないのでcontinueする
        continue
      }

      // リストに文字列が含まれるかどうかによって、listItemかテキストノードとなる
      // リストアイテムノードの場合はそのノードを、そうでない場合は親ノードのlistItemを処理対象とする
      // 例）
      //  リストに文字が入力されている場合のノード構造:
      //    listItemNode
      //      - ExtendedTextNode ← 変数nodeにはこれが入っている
      //  リストに文字が入力されていない場合のノード構造:
      //    listItemNode ← 変数nodeにはこれが入っている
      let processTargetNode
      if ($isListItemNode(node)) {
        processTargetNode = node
      } else {
        processTargetNode = parent
      }

      while (processTargetNode) {
        // ルートノードに到達したらループ終了
        if ($isRootNode(processTargetNode)) {
          break
        }

        // リストアイテムノードの場合、後方の兄弟ノードを取得する
        if ($isListItemNode(processTargetNode)) {
          // 処理対象のリストアイテムノードの、後方の兄弟ノードを取得
          const nextNode = processTargetNode.getNextSibling()

          // 後方の兄弟ノードが存在し、かつリストアイテムノードであるかどうかを確認
          if (nextNode && $isListItemNode(nextNode)) {
            // 後方の兄弟ノードの子ノードがリストノードであるかどうかを確認
            const firstChild = nextNode.getFirstChild()
            if (!$isListNode(firstChild)) {
              // 後方の兄弟ノードの子ノードがリストノードでない場合は、アウトデントしてもひとつ飛ばしのインデント構造にならないため、アウトデントOK
              isOutdentPrevented = false
              break
            } else {
              // 後方の兄弟ノードの子ノードがリストノードの場合は、ひとつ飛ばしのインデント構造ができてしまうため、アウトデント禁止
              isOutdentPrevented = true
              break
            }
          }
        }

        processTargetNode = processTargetNode.getParent()
      }

      // アウトデント禁止が決定したらループを抜ける
      if (isOutdentPrevented) {
        break
      }
    }

    // アウトデントを禁止する場合はコマンドの伝播を停止するためtrueを返す
    if (isOutdentPrevented) {
      return true
    }

    // アウトデントを禁止しない場合はコマンドの伝播を停止しないためfalseを返す
    return false
  }, [])

  useEffect(() => {
    // コマンド登録用の配列
    const registerCommands = []

    registerCommands.push(
      editor.registerCommand(
        INDENT_CONTENT_COMMAND,
        rejectIndentCommandIfNeededListener,
        COMMAND_PRIORITY_CRITICAL
      )
    )
    registerCommands.push(
      editor.registerCommand(
        OUTDENT_CONTENT_COMMAND,
        rejectOutdentCommandIfNeededListener,
        COMMAND_PRIORITY_CRITICAL
      )
    )

    return mergeRegister(...registerCommands)
  }, [
    editor,
    rejectIndentCommandIfNeededListener,
    rejectOutdentCommandIfNeededListener,
  ])

  return null
})
